/**
 * Invoice builder for the Dashboard scan list.
 *
 * READ-ONLY helper: it only reads the scanned lists that the Dashboard already
 * built and calls existing GET endpoints. It never changes scan, save, export
 * or quantity behaviour.
 *
 *   Valid lines   → GET /api/v1/all-products?scan_code=<upc>   (store price book:
 *                   description, unit_case, each_cost, each_retail)
 *   Invalid lines → GET /api/v1/product/lookup-master?upc=<upc> (description, unit_case)
 */
import api from "../api";
import { formatInvalidLabel } from "../constants/scanValidation";

// How much of the long text columns is shown before "..."
const DESCRIPTION_MAX_WORDS = 6;
const DESCRIPTION_MAX_CHARS = 42;

// Parallel lookups at a time (keeps the server comfortable on long lists)
const LOOKUP_CONCURRENCY = 6;

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

/** Keep the first few words; add "..." when anything was cut. */
export const shortenText = (text, maxWords, maxChars) => {
  const clean = String(text ?? "").replace(/\s+/g, " ").trim();
  if (!clean) return "";
  const words = clean.split(" ");
  let short = words.slice(0, maxWords).join(" ");
  if (short.length > maxChars) short = short.slice(0, maxChars).trimEnd();
  return short.length < clean.length ? `${short}...` : short;
};

const toCents = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
export const formatMoney = (cents) => usd.format((cents || 0) / 100);

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Run async fn over items with a fixed number of requests in flight. */
const mapWithLimit = async (items, limit, fn) => {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await fn(items[index], index);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker)
  );
  return results;
};

/* ------------------------------------------------------------------ */
/* Lookups (existing endpoints only)                                   */
/* ------------------------------------------------------------------ */

const fetchStoreProduct = async (upc) => {
  try {
    const { data } = await api.get("/api/v1/all-products", {
      params: { scan_code: upc },
    });
    const match = (data?.products || []).find(
      (p) => String(p.scan_code) === String(upc)
    );
    return { ok: true, product: match || null };
  } catch {
    return { ok: false, product: null };
  }
};

const fetchMasterItem = async (upc) => {
  if (!/^\d+$/.test(String(upc))) return null; // bad characters: nothing to look up
  try {
    const { data } = await api.get("/api/v1/product/lookup-master", {
      params: { upc },
    });
    return data?.found ? data.item : null;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* Build invoice data                                                  */
/* ------------------------------------------------------------------ */

/**
 * @param {Array<{upc, quantity, description}>} products   Dashboard valid list
 * @param {Array<{upc, quantity, description, errorType, invalidReason}>} wrongScans
 */
export const buildInvoiceData = async (products = [], wrongScans = []) => {
  const createdAt = new Date();

  // Same order as on screen (newest scan first) — copied, never mutated
  const validSource = [...products];
  const invalidSource = [...wrongScans];

  const [storeLookups, masterLookups] = await Promise.all([
    mapWithLimit(validSource, LOOKUP_CONCURRENCY, (item) => fetchStoreProduct(item.upc)),
    mapWithLimit(invalidSource, LOOKUP_CONCURRENCY, (item) => fetchMasterItem(item.upc)),
  ]);

  let lineNo = 0;
  let failedLookups = 0;

  const validRows = validSource.map((item, i) => {
    const { ok, product } = storeLookups[i];
    if (!ok) failedLookups += 1;

    const quantity = Number(item.quantity) || 0;
    const eachCostCents = toCents(product?.each_cost);
    const eachRetailCents = toCents(product?.each_retail);
    const description =
      product?.description ||
      (item.description && item.description !== "Not added" ? item.description : "");

    return {
      no: ++lineNo,
      upc: item.upc,
      description,
      unitCase: product?.unit_case ?? "",
      quantity,
      eachCostCents,
      eachRetailCents,
      totalCostCents: eachCostCents * quantity,
      totalRetailCents: eachRetailCents * quantity,
      priced: !!product,
    };
  });

  const invalidRows = invalidSource.map((item, i) => {
    const master = masterLookups[i];
    return {
      no: ++lineNo,
      upc: item.upc,
      description: item.description || master?.description || "",
      unitCase: master?.unit_case ?? "",
      quantity: Number(item.quantity) || 0,
      reason: formatInvalidLabel(item),
    };
  });

  const sum = (rows, key) => rows.reduce((acc, r) => acc + (r[key] || 0), 0);

  const totals = {
    valid: {
      lines: validRows.length,
      quantity: sum(validRows, "quantity"),
      costCents: sum(validRows, "totalCostCents"),
      retailCents: sum(validRows, "totalRetailCents"),
    },
    invalid: {
      lines: invalidRows.length,
      quantity: sum(invalidRows, "quantity"),
    },
  };
  totals.grand = {
    lines: totals.valid.lines + totals.invalid.lines,
    quantity: totals.valid.quantity + totals.invalid.quantity,
    costCents: totals.valid.costCents,
    retailCents: totals.valid.retailCents,
  };

  return { createdAt, validRows, invalidRows, totals, failedLookups };
};

/* ------------------------------------------------------------------ */
/* Render invoice HTML (used by both Preview and Print)                */
/* ------------------------------------------------------------------ */

const DASH = "&mdash;";

const textCell = (full, maxWords, maxChars, className) => {
  const short = shortenText(full, maxWords, maxChars);
  if (!short) return `<td class="${className}">${DASH}</td>`;
  return `<td class="${className}" title="${escapeHtml(full)}">${escapeHtml(short)}</td>`;
};

const validRowHtml = (r) => `
  <tr class="row-valid">
    <td class="c">${r.no}</td>
    <td class="mono">${escapeHtml(r.upc)}</td>
    ${textCell(r.description || "Not added", DESCRIPTION_MAX_WORDS, DESCRIPTION_MAX_CHARS, "txt")}
    <td class="c">${r.unitCase !== "" ? escapeHtml(r.unitCase) : DASH}</td>
    <td class="c b">${r.quantity}</td>
    <td class="r">${r.priced ? formatMoney(r.eachCostCents) : DASH}</td>
    <td class="r">${r.priced ? formatMoney(r.eachRetailCents) : DASH}</td>
    <td class="r b">${r.priced ? formatMoney(r.totalCostCents) : DASH}</td>
    <td class="r b">${r.priced ? formatMoney(r.totalRetailCents) : DASH}</td>
  </tr>`;

const invalidRowHtml = (r) => {
  const short = shortenText(r.description, DESCRIPTION_MAX_WORDS, DESCRIPTION_MAX_CHARS);
  const titleText = r.description ? `${r.description} — ${r.reason}` : r.reason;
  return `
  <tr class="row-invalid">
    <td class="c">${r.no}</td>
    <td class="mono">${escapeHtml(r.upc)}</td>
    <td class="txt" title="${escapeHtml(titleText)}">${short ? `${escapeHtml(short)} ` : ""}<span class="tag">${escapeHtml(r.reason)}</span></td>
    <td class="c">${r.unitCase !== "" ? escapeHtml(r.unitCase) : DASH}</td>
    <td class="c b">${r.quantity}</td>
    <td class="r">${DASH}</td>
    <td class="r">${DASH}</td>
    <td class="r">${DASH}</td>
    <td class="r">${DASH}</td>
  </tr>`;
};

const formatCreatedAt = (date) =>
  date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });

/**
 * @param {object} data      result of buildInvoiceData
 * @param {object} [meta]    { preparedBy }
 */
export const renderInvoiceHtml = (data, meta = {}) => {
  const { createdAt, validRows, invalidRows, totals, failedLookups } = data;
  const unpricedValid = validRows.filter((r) => !r.priced).length;

  const notes = [];
  if (invalidRows.length > 0) {
    notes.push("Invalid products are not in the store price book, so they have no cost or retail and add $0.00 to the totals.");
  }
  if (unpricedValid > 0) {
    notes.push(`${unpricedValid} valid item${unpricedValid !== 1 ? "s" : ""} could not be priced${failedLookups ? " (price lookup failed — check your connection and preview again)" : ""} and add $0.00 to the totals.`);
  }

  const sectionRow = (cls, icon, label, count, qty) => `
    <tr class="section ${cls}">
      <td colspan="9">${icon} ${label} <span class="muted">— ${count} item${count !== 1 ? "s" : ""}, ${qty} unit${qty !== 1 ? "s" : ""}</span></td>
    </tr>`;

  const body = [
    validRows.length
      ? sectionRow("sec-valid", "&#10003;", "VALID PRODUCTS", totals.valid.lines, totals.valid.quantity) +
        validRows.map(validRowHtml).join("")
      : "",
    invalidRows.length
      ? sectionRow("sec-invalid", "&#10007;", "INVALID PRODUCTS", totals.invalid.lines, totals.invalid.quantity) +
        invalidRows.map(invalidRowHtml).join("")
      : "",
  ].join("");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Invoice — ${escapeHtml(formatCreatedAt(createdAt))}</title>
<style>
  @page { size: letter landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; background: #fff; color: #1f2937; }
  body {
    font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    font-size: 11px; padding: 18px;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  @media print { body { padding: 0; } }

  .head { display: flex; justify-content: space-between; align-items: flex-end;
          border-bottom: 2px solid #1f2937; padding-bottom: 8px; margin-bottom: 12px; }
  .head h1 { margin: 0; font-size: 22px; letter-spacing: 2px; }
  .meta { text-align: right; line-height: 1.6; }
  .meta b { color: #111827; }

  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  thead { display: table-header-group; }
  th { background: #f3f4f6; text-transform: uppercase; font-size: 9.5px; letter-spacing: .4px;
       color: #374151; padding: 6px 5px; border-bottom: 1px solid #9ca3af; text-align: left;
       white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  td { padding: 5px; border-bottom: 1px solid #e5e7eb; white-space: nowrap;
       overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
  tr { page-break-inside: avoid; }
  th.c, td.c { text-align: center; }
  th.r, td.r { text-align: right; }
  td.b { font-weight: 600; }
  td.mono { font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace; font-size: 10.5px; }

  .section td { font-weight: 700; font-size: 11px; padding: 7px 6px; letter-spacing: .3px; border-bottom: none; }
  .sec-valid td { background: #ecfdf5; color: #047857; border-top: 2px solid #10b981; }
  .sec-invalid td { background: #fef2f2; color: #b91c1c; border-top: 2px solid #ef4444; }
  .section .muted { font-weight: 500; opacity: .8; }
  .row-valid td:first-child { border-left: 3px solid #10b981; }
  .row-invalid td:first-child { border-left: 3px solid #ef4444; }
  .row-invalid td { color: #7f1d1d; background: #fffafa; }
  .tag { display: inline-block; font-size: 9px; font-weight: 700; color: #b91c1c;
         border: 1px solid #fca5a5; background: #fee2e2; border-radius: 3px; padding: 0 4px; }

  .totals { margin-top: 14px; margin-left: auto; width: 62%; border-collapse: collapse; page-break-inside: avoid; }
  .totals th, .totals td { padding: 6px 8px; border: 1px solid #d1d5db; white-space: nowrap; }
  .totals th { background: #f9fafb; }
  .totals td { text-align: right; }
  .totals td.label { text-align: left; font-weight: 600; }
  .totals tr.grand td { background: #111827; color: #fff; font-weight: 700; font-size: 12px; }
  .notes { margin-top: 10px; color: #6b7280; font-size: 10px; line-height: 1.5; }
</style>
</head>
<body>
  <div class="head">
    <h1>INVOICE</h1>
    <div class="meta">
      <div>Created: <b>${escapeHtml(formatCreatedAt(createdAt))}</b></div>
      ${meta.preparedBy ? `<div>Prepared by: <b>${escapeHtml(meta.preparedBy)}</b></div>` : ""}
    </div>
  </div>

  <table>
    <colgroup>
      <col style="width:6%"><col style="width:12%"><col style="width:30%">
      <col style="width:7%"><col style="width:6%"><col style="width:9%"><col style="width:9%">
      <col style="width:10.5%"><col style="width:10.5%">
    </colgroup>
    <thead>
      <tr>
        <th class="c">Item #</th><th>UPC</th><th>Description</th>
        <th class="c">Unit/Case</th><th class="c">Qty</th>
        <th class="r">Each Cost</th><th class="r">Each Retail</th>
        <th class="r">Total Cost</th><th class="r">Total Retail</th>
      </tr>
    </thead>
    <tbody>${body}</tbody>
  </table>

  <table class="totals">
    <thead>
      <tr><th></th><th class="r">Products</th><th class="r">Quantity</th><th class="r">Total Cost</th><th class="r">Total Retail</th></tr>
    </thead>
    <tbody>
      <tr><td class="label">Valid products</td><td>${totals.valid.lines}</td><td>${totals.valid.quantity}</td>
          <td>${formatMoney(totals.valid.costCents)}</td><td>${formatMoney(totals.valid.retailCents)}</td></tr>
      <tr><td class="label">Invalid products</td><td>${totals.invalid.lines}</td><td>${totals.invalid.quantity}</td>
          <td>${DASH}</td><td>${DASH}</td></tr>
      <tr class="grand"><td class="label">GRAND TOTAL</td><td>${totals.grand.lines}</td><td>${totals.grand.quantity}</td>
          <td>${formatMoney(totals.grand.costCents)}</td><td>${formatMoney(totals.grand.retailCents)}</td></tr>
    </tbody>
  </table>

  ${notes.length ? `<div class="notes">${notes.map((n) => `<div>* ${escapeHtml(n)}</div>`).join("")}</div>` : ""}
</body>
</html>`;
};